<!-- Admin Main Area Component - Redesigned Layout -->
<div class="admin-main-area">

  <!-- Upload Progress Popup - Always available -->
  <app-upload-progress-popup></app-upload-progress-popup>

  <!-- Header with breadcrumb and status -->
  <div class="main-header">
    <!-- Breadcrumb Navigation -->
    <div class="breadcrumb-section">
      <mat-icon class="breadcrumb-icon">explore</mat-icon>
      <ng-container *ngIf="breadcrumbItems.length > 0; else noBreadcrumb">
        <ng-container *ngFor="let item of breadcrumbItems; let last = last">
          <div class="breadcrumb-item">
            <mat-icon class="item-icon">{{ item.icon }}</mat-icon>
            <span class="item-name">{{ item.name }}</span>
          </div>
          <mat-icon *ngIf="!last" class="breadcrumb-separator">chevron_right</mat-icon>
        </ng-container>
      </ng-container>
      <ng-template #noBreadcrumb>
        <span class="no-selection-text">No operation selected</span>
      </ng-template>
    </div>

    <!-- Action Status -->
    <div class="action-status">
      <div *ngIf="isLoading" class="status-loading">
        <mat-icon class="spinning">refresh</mat-icon>
        <span>{{ getStatusMessage() }}</span>
      </div>
      <div *ngIf="!isLoading && operationResult" class="status-success">
        <mat-icon>check_circle</mat-icon>
        <span>Operation completed</span>
      </div>
    </div>

    <!-- Header Actions -->
    <div class="header-actions">
      <button mat-icon-button
              *ngIf="showProcessedFiles"
              (click)="onRefreshBuckets()"
              matTooltip="Refresh Data"
              class="action-button">
        <mat-icon>refresh</mat-icon>
      </button>

      <mat-checkbox
        *ngIf="!showDetails && processedFiles.length > 0"
        [checked]="showDetails"
        (change)="onShowDetailsChange()"
        color="primary"
        class="show-details-toggle">
        Show Details
      </mat-checkbox>
    </div>
  </div>

  <!-- No Operation Selected State -->
  <div *ngIf="!selectedNode" class="empty-state">
    <mat-icon class="empty-icon">admin_panel_settings</mat-icon>
    <h2>Welcome to Admin Panel</h2>
    <p>Select an operation from the tree navigation to get started.</p>
  </div>

  <!-- Image Processing Tool (Full Screen Mode) -->
  <div *ngIf="selectedNode && isImageProcessingTool()" class="image-processing-tool-wrapper">
    <app-image-processing-tool></app-image-processing-tool>
  </div>

  <!-- GCP-Style Bucket Browser (Full Width Layout) -->
  <div *ngIf="selectedNode && isGcpUploadModify()" class="gcp-bucket-browser">
    
    <!-- Top Navigation Bar (like GCP) -->
    <div class="gcp-nav-bar">
      <!-- Tab Navigation -->
      <div class="gcp-tabs">
        <button class="gcp-tab active">
          <span>Objects</span>
        </button>
        <button class="gcp-tab" disabled>
          <span>Configuration</span>
        </button>
        <button class="gcp-tab" disabled>
          <span>Permissions</span>
        </button>
        <button class="gcp-tab" disabled>
          <span>Lifecycle</span>
        </button>
      </div>
    </div>

    <!-- Breadcrumb Navigation -->
    <div class="gcp-breadcrumb-bar">
      <div class="gcp-breadcrumb">
        <span class="breadcrumb-label">Buckets</span>
        <mat-icon class="breadcrumb-arrow">chevron_right</mat-icon>
        <span class="breadcrumb-bucket" (click)="onNavigateToBucketRoot()">{{ bucketName }}</span>
        <ng-container *ngFor="let crumb of getCurrentPathBreadcrumbs(); let last = last">
          <mat-icon class="breadcrumb-arrow">chevron_right</mat-icon>
          <span class="breadcrumb-folder" 
                [class.active]="last"
                (click)="onNavigateToPath(crumb.path)">{{ crumb.name }}</span>
        </ng-container>
      </div>
      <button mat-icon-button 
              class="copy-path-btn"
              (click)="onCopyCurrentPath()"
              matTooltip="Copy path">
        <mat-icon>content_copy</mat-icon>
      </button>
    </div>

    <!-- Folder Download Progress Bar -->
    <div class="gcp-download-progress" *ngIf="isDownloadingFolder">
      <div class="download-progress-content">
        <mat-icon class="download-icon spinning">cloud_download</mat-icon>
        <div class="download-info">
          <span class="download-text">
            {{ downloadStatus === 'preparing' ? 'Preparing' : 'Downloading' }} "{{ downloadingFolderName }}.zip"
            <span *ngIf="downloadStatus === 'downloading'">... {{ downloadProgress }}%</span>
          </span>
          <mat-progress-bar 
            [mode]="downloadStatus === 'preparing' ? 'indeterminate' : 'determinate'"
            [value]="downloadProgress">
          </mat-progress-bar>
        </div>
      </div>
    </div>

    <!-- Horizontal Action Bar -->
    <div class="gcp-action-bar">
      <button mat-stroked-button 
              class="gcp-action-btn"
              (click)="onCreateRootFolder()"
              [disabled]="isLoadingHierarchy">
        <mat-icon>create_new_folder</mat-icon>
        <span>Create folder</span>
      </button>
      
      <div class="gcp-upload-menu">
        <button mat-stroked-button 
                class="gcp-action-btn upload-btn"
                [matMenuTriggerFor]="uploadMenu">
          <mat-icon>upload</mat-icon>
          <span>Upload</span>
          <mat-icon class="dropdown-icon">arrow_drop_down</mat-icon>
        </button>
        <mat-menu #uploadMenu="matMenu" class="gcp-upload-menu-panel">
          <button mat-menu-item (click)="onUploadToRoot()">
            <mat-icon>upload_file</mat-icon>
            <span>Upload files</span>
          </button>
          <button mat-menu-item (click)="onUploadFolderToRoot()">
            <mat-icon>drive_folder_upload</mat-icon>
            <span>Upload folder</span>
          </button>
        </mat-menu>
      </div>

      <button mat-stroked-button 
              class="gcp-action-btn"
              (click)="onDownloadSelectedItem()"
              [disabled]="getSelectedCount() === 0"
              [matTooltip]="getSelectedCount() > 0 ? 'Download ' + getSelectedCount() + ' item(s)' : 'Select items to download'">
        <mat-icon>download</mat-icon>
        <span>Download{{ getSelectedCount() > 0 ? ' (' + getSelectedCount() + ')' : '' }}</span>
      </button>
      
      <button mat-stroked-button 
              class="gcp-action-btn delete-btn"
              (click)="onDeleteSelected()"
              [disabled]="getSelectedCount() === 0"
              [matTooltip]="getSelectedCount() > 0 ? 'Delete ' + getSelectedCount() + ' item(s)' : 'Select items to delete'">
        <mat-icon>delete</mat-icon>
        <span>Delete{{ getSelectedCount() > 0 ? ' (' + getSelectedCount() + ')' : '' }}</span>
      </button>

      <button mat-stroked-button 
              class="gcp-action-btn clear-selection-btn"
              (click)="onClearSelection()"
              [disabled]="getSelectedCount() === 0"
              [matTooltip]="getSelectedCount() > 0 ? 'Clear selection' : 'No items selected'">
        <mat-icon>deselect</mat-icon>
        <span>Clear</span>
      </button>

      <div class="gcp-action-spacer"></div>

      <button mat-icon-button 
              class="gcp-refresh-btn"
              (click)="onRefreshHierarchy()"
              [disabled]="isLoadingHierarchy"
              [class.spinning]="isLoadingHierarchy"
              matTooltip="Refresh">
        <mat-icon>refresh</mat-icon>
      </button>
    </div>

    <!-- Main Content Area with Left Tree and Right File List -->
    <div class="gcp-main-content">
      
      <!-- Left Panel: Folder Browser (Tree) -->
      <div class="gcp-folder-browser" [class.collapsed]="folderBrowserCollapsed">
        <div class="folder-browser-header">
          <span>Folder browser</span>
          <button mat-icon-button 
                  (click)="toggleFolderBrowser()"
                  matTooltip="Toggle folder browser">
            <mat-icon>{{ folderBrowserCollapsed ? 'chevron_right' : 'chevron_left' }}</mat-icon>
          </button>
        </div>
        
        <div class="folder-tree-container" *ngIf="!folderBrowserCollapsed">
          <!-- Bucket Root -->
          <div class="folder-tree-item root-item"
               [class.selected]="!selectedHierarchyNode"
               (click)="onNavigateToBucketRoot()">
            <mat-icon class="folder-icon bucket-icon">cloud</mat-icon>
            <span class="folder-name">{{ bucketName }}</span>
          </div>
          
          <!-- Loading State -->
          <div *ngIf="isLoadingHierarchy" class="folder-tree-loading">
            <mat-progress-spinner diameter="24" mode="indeterminate"></mat-progress-spinner>
          </div>
          
          <!-- Folder Tree -->
          <div *ngIf="!isLoadingHierarchy" class="folder-tree-content">
            <ng-container *ngTemplateOutlet="gcpFolderTreeNode; context: { $implicit: getFoldersOnly(bucketHierarchy), level: 0 }"></ng-container>
          </div>
        </div>
      </div>

      <!-- Right Panel: File/Folder List -->
      <div class="gcp-file-list-panel">
        
        <!-- Filter Bar -->
        <div class="gcp-filter-bar">
          <div class="filter-left">
            <mat-form-field appearance="outline" class="filter-input">
              <mat-icon matPrefix>filter_list</mat-icon>
              <input matInput placeholder="Filter objects and folders" [(ngModel)]="fileFilter">
            </mat-form-field>
          </div>
          <div class="filter-right">
            <span class="item-count" *ngIf="!isLoadingHierarchy">
              {{ getCurrentFolderItems().length }} items
            </span>
          </div>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoadingHierarchy" class="gcp-loading-state">
          <mat-progress-spinner diameter="40" mode="indeterminate"></mat-progress-spinner>
          <span>Loading...</span>
        </div>

        <!-- File List Table -->
        <div *ngIf="!isLoadingHierarchy" class="gcp-file-table">
          <table class="gcp-table">
            <thead>
              <tr>
                <th class="checkbox-col">
                  <mat-checkbox 
                    [checked]="allItemsSelected"
                    [indeterminate]="someItemsSelected"
                    (change)="onSelectAllItems($event.checked)">
                  </mat-checkbox>
                </th>
                <th class="name-col" (click)="onSortBy('name')">
                  Name
                  <mat-icon *ngIf="sortColumn === 'name'" class="sort-icon">
                    {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                  </mat-icon>
                </th>
                <th class="size-col" (click)="onSortBy('size')">
                  Size
                  <mat-icon *ngIf="sortColumn === 'size'" class="sort-icon">
                    {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                  </mat-icon>
                </th>
                <th class="type-col">Type</th>
                <th class="modified-col" (click)="onSortBy('lastModified')">
                  Last modified
                  <mat-icon *ngIf="sortColumn === 'lastModified'" class="sort-icon">
                    {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                  </mat-icon>
                </th>
                <th class="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <!-- Empty State -->
              <tr *ngIf="getFilteredCurrentFolderItems().length === 0" class="empty-row">
                <td colspan="6">
                  <div class="empty-folder-state">
                    <mat-icon>folder_open</mat-icon>
                    <span>This folder is empty</span>
                  </div>
                </td>
              </tr>
              
              <!-- File/Folder Rows -->
              <tr *ngFor="let item of getFilteredCurrentFolderItems()" 
                  class="file-row"
                  [class.selected]="isItemSelected(item)"
                  [class.folder-row]="item.type === 'folder'"
                  (dblclick)="onItemDoubleClick(item)">
                <td class="checkbox-col" (click)="$event.stopPropagation()">
                  <mat-checkbox 
                    [checked]="isItemSelected(item)"
                    (change)="onItemCheckboxChange(item, $event.checked)">
                  </mat-checkbox>
                </td>
                <td class="name-col">
                  <div class="name-cell">
                    <mat-icon class="item-icon" [class.folder-icon]="item.type === 'folder'">
                      {{ item.type === 'folder' ? 'folder' : getFileTypeIcon(item.name) }}
                    </mat-icon>
                    <!-- Folder: click to navigate -->
                    <span *ngIf="item.type === 'folder'" 
                          class="item-name folder-name"
                          (click)="onNavigateToFolder(item)">
                      {{ item.name }}/
                    </span>
                    <!-- File: click to preview -->
                    <span *ngIf="item.type === 'file'" 
                          class="item-name file-link"
                          (click)="onPreviewFile(item)">
                      {{ item.name }}
                    </span>
                  </div>
                </td>
                <td class="size-col">
                  {{ item.type === 'file' && item.size ? formatFileSize(item.size) : '—' }}
                </td>
                <td class="type-col">
                  {{ item.type === 'folder' ? 'Folder' : getFileType(item.name) }}
                </td>
                <td class="modified-col">
                  {{ item.lastModified ? (item.lastModified | date:'MMM d, y, h:mm:ss a') : '—' }}
                </td>
                <td class="actions-col" (click)="$event.stopPropagation()">
                  <button mat-icon-button 
                          [matMenuTriggerFor]="itemActionsMenu"
                          matTooltip="More actions">
                    <mat-icon>more_vert</mat-icon>
                  </button>
                  <mat-menu #itemActionsMenu="matMenu">
                    <button mat-menu-item *ngIf="item.type === 'folder'" (click)="onNavigateToFolder(item)">
                      <mat-icon>folder_open</mat-icon>
                      <span>Open</span>
                    </button>
                    <button mat-menu-item *ngIf="item.type === 'folder'" (click)="onCreateFolderInPath(item.path)">
                      <mat-icon>create_new_folder</mat-icon>
                      <span>Create subfolder</span>
                    </button>
                    <button mat-menu-item *ngIf="item.type === 'folder'" (click)="onUploadToFolder(item.path)">
                      <mat-icon>upload_file</mat-icon>
                      <span>Upload files here</span>
                    </button>
                    <mat-divider></mat-divider>
                    <button mat-menu-item (click)="item.type === 'file' ? onDownloadFile(item) : onDownloadFolder(item)">
                      <mat-icon>download</mat-icon>
                      <span>Download</span>
                    </button>
                    <button mat-menu-item (click)="onCopyItemPath(item)">
                      <mat-icon>content_copy</mat-icon>
                      <span>Copy path</span>
                    </button>
                    <mat-divider></mat-divider>
                    <button mat-menu-item class="delete-action" (click)="onDeleteNode(item)">
                      <mat-icon>delete</mat-icon>
                      <span>Delete</span>
                    </button>
                  </mat-menu>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination / Info Bar -->
        <div class="gcp-pagination-bar" *ngIf="!isLoadingHierarchy && getFilteredCurrentFolderItems().length > 0">
          <span class="pagination-info">
            Showing {{ getFilteredCurrentFolderItems().length }} of {{ getCurrentFolderItems().length }} items
          </span>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content Layout (for other operations) -->
  <div *ngIf="selectedNode && !isImageProcessingTool()" class="main-content">

    <!-- Image Navigation Controls Panel (only for image operations) -->
    <div *ngIf="isImageOperations() && imageProcessingMode && processingImages.length > 0" class="image-navigation-panel">
      <div class="navigation-controls">
        <!-- Previous Button -->
        <button mat-icon-button
                [disabled]="!hasPreviousImage()"
                (click)="onPreviousImage()"
                matTooltip="Previous Image"
                class="nav-button">
          <mat-icon>arrow_back</mat-icon>
        </button>

        <!-- Jump to Coordinates Button -->
        <button mat-icon-button
                [disabled]="!hasValidCoordinates()"
                (click)="jumpToCoordinates()"
                matTooltip="Jump to Coordinates"
                class="nav-button jump-button">
          <mat-icon>my_location</mat-icon>
        </button>

        <!-- Horizontal Line Controls -->
        <button mat-icon-button
                (click)="toggleLineControls()"
                [matTooltip]="showLineControls ? 'Disable Line Mode' : 'Enable Line Mode'"
                class="nav-button line-toggle-button"
                [class.active]="showLineControls">
          <mat-icon>horizontal_rule</mat-icon>
        </button>

        <!-- Fullscreen Toggle Button -->
        <button mat-button
                (click)="toggleFullscreen()"
                [matTooltip]="isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'"
                class="nav-button fullscreen-button"
                color="primary">
          {{ isFullscreen ? 'EXIT FS' : 'FULLSCREEN' }}
        </button>

        <button mat-icon-button
                [disabled]="!showLineControls"
                (click)="addHorizontalLineFromInput()"
                matTooltip="Add Horizontal Line from Input"
                class="nav-button add-line-button">
          <mat-icon>add</mat-icon>
        </button>

        <button mat-icon-button
                [disabled]="horizontalLines.length === 0"
                (click)="clearAllHorizontalLines()"
                matTooltip="Clear All Lines"
                class="nav-button clear-lines-button">
          <mat-icon>clear</mat-icon>
        </button>

        <button mat-icon-button
                [disabled]="!hasUnsavedLines || isSavingLines"
                (click)="saveAllLines()"
                [matTooltip]="isSavingLines ? 'Saving...' : 'Save Lines to Database'"
                class="nav-button save-lines-button"
                [class.active]="hasUnsavedLines">
          <mat-icon>{{ isSavingLines ? 'hourglass_empty' : 'save' }}</mat-icon>
        </button>

        <!-- Next Button -->
        <button mat-icon-button
                [disabled]="!hasNextImage()"
                (click)="onNextImage()"
                matTooltip="Next Image"
                class="nav-button">
          <mat-icon>arrow_forward</mat-icon>
        </button>
      </div>

      <!-- Navigation Info -->
      <div class="navigation-info">
        <div class="info-item">
          <span class="info-label">Position:</span>
          <span class="info-value">{{ currentImageIndex + 1 }} of {{ processingImages.length }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Coordinates:</span>
          <span *ngIf="mouseCoordinates.x >= 0 && mouseCoordinates.y >= 0; else noCoords" class="info-value">
            ({{ mouseCoordinates.x }}, {{ mouseCoordinates.y }})
          </span>
          <ng-template #noCoords>
            <span class="info-value no-coords">Hover over image</span>
          </ng-template>
        </div>
        <div class="info-item" *ngIf="horizontalLines.length > 0">
          <span class="info-label">Lines:</span>
          <span class="info-value">{{ horizontalLines.length }} drawn</span>
        </div>
      </div>
    </div>

    <!-- Two-Panel Layout -->
    <div class="content-panels">

      <!-- Left Panel: Actions & Folder Selection -->
      <div class="left-panel">

        <!-- Watermark Removal Interface -->
        <div *ngIf="isWatermarkRemoval()" class="watermark-interface">

          <!-- Service Info -->
          <div class="service-info">
            <div class="service-header">
              <mat-icon class="service-icon">cleaning_services</mat-icon>
              <div class="service-details">
                <h3>Watermark Removal</h3>
                <p class="service-description">Advanced AI-powered watermark removal from images</p>
                <div class="bucket-info">
                  <mat-icon>cloud</mat-icon>
                  <span>Bucket: {{ bucketName }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Folder Selection -->
          <div class="folder-selection">
            <div class="section-header">
              <mat-icon>folder</mat-icon>
              <h4>Select Folder for Processing</h4>
              <span class="folder-count" *ngIf="bucketFolders.length > 0">{{ bucketFolders.length }} folders</span>
            </div>

            <!-- Loading State -->
            <div *ngIf="isLoading && bucketFolders.length === 0" class="loading-state">
              <mat-progress-spinner diameter="24" mode="indeterminate"></mat-progress-spinner>
              <span>Loading folders...</span>
            </div>

            <!-- Folder List -->
            <div *ngIf="bucketFolders.length > 0" class="folder-list">
              <div *ngFor="let folder of bucketFolders"
                   class="folder-item"
                   [class.selected]="folder === selectedFolder"
                   (click)="onFolderClick(folder)">
                <mat-icon class="folder-icon">folder</mat-icon>
                <span class="folder-name">{{ folder }}</span>
                <mat-icon *ngIf="folder === selectedFolder" class="check-icon">check_circle</mat-icon>
              </div>
            </div>

            <!-- No Folders State -->
            <div *ngIf="bucketFolders.length === 0 && !isLoading" class="no-folders">
              <mat-icon>folder_off</mat-icon>
              <p>No folders found</p>
              <button mat-button color="primary" (click)="onRefreshBuckets()">
                <mat-icon>refresh</mat-icon>
                Refresh
              </button>
            </div>
          </div>

          <!-- Process Action -->
          <div class="process-action" *ngIf="selectedFolder">
            <button mat-raised-button
                    color="primary"
                    (click)="onProcessFolder()"
                    [disabled]="isLoading"
                    class="process-button">
              <mat-icon>cleaning_services</mat-icon>
              Remove Watermarks
            </button>
          </div>
        </div>

        <!-- Other OCR Interfaces -->
        <div *ngIf="isGoogleVisionOCR() || isTesseractOCR() || isMistralOCR()" class="ocr-interface">
          <div class="service-info">
            <div class="service-header">
              <mat-icon class="service-icon">
                {{ isGoogleVisionOCR() ? 'visibility' : isTesseractOCR() ? 'text_fields' : 'auto_awesome' }}
              </mat-icon>
              <div class="service-details">
                <h3>
                  {{ isGoogleVisionOCR() ? 'Google Vision OCR' : isTesseractOCR() ? 'Tesseract OCR' : 'Mistral AI OCR' }}
                </h3>
                <p class="service-description">
                  {{ getOCRDescription() }}
                </p>
              </div>
            </div>
          </div>

          <div class="coming-soon">
            <mat-icon>construction</mat-icon>
            <p>This OCR service will be implemented in a future update.</p>
            <button mat-button disabled>Coming Soon</button>
          </div>
        </div>

        <!-- Image Operations Info Panel (moved to left panel) -->


        <!-- Image Operations Basic Info (when not in processing mode) -->
        <div *ngIf="isImageOperations() && !imageProcessingMode" class="image-operations-interface">

          <!-- Service Info -->
          <div class="service-info">
            <div class="service-header">
              <mat-icon class="service-icon">image</mat-icon>
              <div class="service-details">
                <h3>Image Operations</h3>
                <p class="service-description">{{ getImageInstructions() }}</p>
                <div class="bucket-info">
                  <mat-icon>cloud</mat-icon>
                  <span>Bucket: {{ bucketName }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Image Processing Mode Toggle -->
          <div class="processing-mode-toggle">
            <div class="section-header">
              <mat-icon>visibility</mat-icon>
              <h4>Enter Image Processing Mode</h4>
            </div>
            <mat-checkbox
              [(ngModel)]="imageProcessingMode"
              (change)="onImageProcessingModeToggle($event.checked)"
              color="primary"
              class="mode-checkbox">
              Enable Image Processing Mode
            </mat-checkbox>
          </div>

          <!-- Sync Action -->
          <div class="sync-action">
            <button mat-raised-button
                    color="primary"
                    (click)="onSyncImages()"
                    [disabled]="isLoadingImages"
                    class="sync-button">
              <mat-icon>sync</mat-icon>
              Sync Images from Bucket
            </button>
          </div>
        </div>

        <!-- Database & System Settings -->
        <div *ngIf="isDatabaseOperation() || isSystemSettingsOperation()" class="other-interface">
          <div class="service-info">
            <div class="service-header">
              <mat-icon class="service-icon">
                {{ isDatabaseOperation() ? 'storage' : 'settings' }}
              </mat-icon>
              <div class="service-details">
                <h3>{{ selectedNode.name }}</h3>
                <p class="service-description">{{ getCurrentOperationDescription() }}</p>
              </div>
            </div>
          </div>

          <div class="coming-soon">
            <mat-icon>construction</mat-icon>
            <p>This feature will be implemented in a future update.</p>
            <button mat-button disabled>Coming Soon</button>
          </div>
        </div>
      </div>

      <!-- Right Panel: Image Viewer or Processed Files -->
      <div class="right-panel">

        <!-- Image Operations Viewer (moved to right panel) -->
        <div *ngIf="isImageOperations() && imageProcessingMode" class="image-display-section">

          <!-- Loading State -->
          <div *ngIf="isLoadingImages" class="loading-state">
            <mat-progress-spinner diameter="32" mode="indeterminate"></mat-progress-spinner>
            <span>Loading images...</span>
          </div>

          <!-- No Images State -->
          <div *ngIf="!isLoadingImages && processingImages.length === 0" class="no-images">
            <mat-icon>image_search</mat-icon>
            <p>No images found</p>
            <button mat-button color="primary" (click)="onSyncImages()">
              <mat-icon>sync</mat-icon>
              Sync Images
            </button>
          </div>

          <!-- Image Viewer -->
          <div *ngIf="!isLoadingImages && currentImage" class="image-viewer">

            <!-- Image Container with Error Handling -->
            <div class="image-container">
              <img
                [src]="getCurrentImageDisplayUrl()"
                [alt]="currentImage.image_name"
                class="processing-image"
                [class.line-mode]="showLineControls"
                (mousemove)="onImageMouseMove($event)"
                (click)="onImageClick($event)"
                (load)="onImageLoad($event)"
                (error)="onImageError($event)"
                #imageElement />

              <!-- Image Loading Overlay -->
              <div *ngIf="isImageLoading" class="image-loading-overlay">
                <mat-progress-spinner diameter="40" mode="indeterminate"></mat-progress-spinner>
                <span>Loading image...</span>
              </div>

              <!-- Image Error Overlay -->
              <div *ngIf="imageError" class="image-error-overlay">
                <mat-icon>broken_image</mat-icon>
                <span>Failed to load image</span>
                <small>{{ imageError }}</small>
              </div>
            </div>

            <!-- Image Info -->
            <div class="image-info">
              <span class="info-text">{{ getImageInfo() }}</span>
              <span class="coordinates" *ngIf="mouseCoordinates.x >= 0 && mouseCoordinates.y >= 0">
                <mat-icon>my_location</mat-icon>
                ({{ mouseCoordinates.x }}, {{ mouseCoordinates.y }})
              </span>
            </div>
          </div>


        </div>

        <!-- Processed Files Section (for other operations) -->
        <div *ngIf="showProcessedFiles && !isImageOperations()" class="processed-files-section">

          <!-- Section Header -->
          <div class="section-header">
            <mat-icon>image</mat-icon>
            <h4>Processed Files</h4>
            <span class="file-count" *ngIf="processedFiles.length > 0">{{ processedFiles.length }} files</span>
          </div>

          <!-- Loading Processed Files -->
          <div *ngIf="isLoading && processedFiles.length === 0" class="loading-state">
            <mat-progress-spinner diameter="32" mode="indeterminate"></mat-progress-spinner>
            <span>Processing files...</span>
          </div>

          <!-- Files Grid -->
          <div *ngIf="processedFiles.length > 0" class="files-grid">
            <div *ngFor="let file of processedFiles"
                 class="file-card"
                 [class.selected]="file === selectedFile"
                 (click)="onFileClick(file)">

              <!-- File Action Buttons -->


              <!-- File Info -->
              <div class="file-info">
                <div class="file-id">{{ file.page_id }}</div>
                <div class="file-book">{{ file.book_name }}</div>
                <div class="file-status" *ngIf="getFileStatusText(file)">
                  <mat-icon class="status-icon"
                           [class.completed]="file.watermark_removed_url"
                           [class.reprocessed]="file.reprocess_count > 0"
                           [class.original]="!file.watermark_removed_url">
                    {{ getFileStatusIcon(file) }}
                  </mat-icon>
                  <span>{{ getFileStatusText(file) }}</span>
                </div>
                <div class="file-validation">
                  <mat-icon
                    class="validation-icon"
                    [class.correct]="file.validation === 'Correct'"
                    [class.incorrect]="file.validation === 'Incorrect'">
                    {{ file.validation === 'Correct' ? 'verified' : 'warning' }}
                  </mat-icon>
                  <span class="validation-text">{{ file.validation }}</span>
                </div>
              </div>

              <!-- Selection Indicator -->
              <div *ngIf="file === selectedFile" class="selection-indicator">
                <mat-icon>check_circle</mat-icon>
              </div>
            </div>
          </div>

          <!-- No Files State -->
          <div *ngIf="processedFiles.length === 0 && !isLoading" class="no-files">
            <mat-icon>image_search</mat-icon>
            <h5>No processed files</h5>
            <p>Select a folder and process it to see results here.</p>
          </div>
        </div>

        <!-- Operation Results -->
        <div *ngIf="operationResult" class="operation-results">
          <div class="results-header">
            <mat-icon>assignment_turned_in</mat-icon>
            <h4>Operation Results</h4>
          </div>
          <div class="results-content">
            <pre>{{ operationResult }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Fullscreen Image Overlay -->
<div *ngIf="isFullscreen" class="fullscreen-overlay" (click)="toggleFullscreen()">
  <div class="fullscreen-container" (click)="$event.stopPropagation()">
    <!-- Fullscreen Header -->
    <div class="fullscreen-header">
      <h2>{{ currentImage?.image_name }} - Fullscreen View</h2>
      <div class="fullscreen-controls">
        <!-- Line Controls in Fullscreen -->
        <button mat-icon-button
                (click)="toggleLineControls()"
                [matTooltip]="showLineControls ? 'Disable Line Mode' : 'Enable Line Mode'"
                class="fullscreen-button line-toggle-button"
                [class.active]="showLineControls">
          <mat-icon>horizontal_rule</mat-icon>
        </button>
        
        <!-- Close Fullscreen -->
        <button mat-icon-button
                (click)="toggleFullscreen()"
                matTooltip="Exit Fullscreen"
                class="fullscreen-button">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </div>

    <!-- Fullscreen Image Container -->
    <div class="fullscreen-image-container image-container">
      <img
        [src]="fullscreenImageUrl"
        [alt]="currentImage?.image_name"
        class="fullscreen-image processing-image"
        [class.line-mode]="showLineControls"
        (mousemove)="onImageMouseMove($event)"
        (click)="onImageClick($event)"
        (load)="onImageLoad($event)" />
    </div>

    <!-- Fullscreen Image Info -->
    <div class="fullscreen-info">
      <span>{{ getImageInfo() }}</span>
      <span *ngIf="mouseCoordinates.x >= 0 && mouseCoordinates.y >= 0" class="coordinates">
        <mat-icon>my_location</mat-icon>
        ({{ mouseCoordinates.x }}, {{ mouseCoordinates.y }})
      </span>
      <span *ngIf="showLineControls" class="line-mode-status">
        Line Mode: ON ({{ horizontalLines.length }} lines)
      </span>
    </div>
  </div>
</div>

<!-- Hierarchical Tree Template -->
<ng-template #hierarchyNode let-nodes let-level="level">
  <div *ngFor="let node of nodes" class="hierarchy-item">
    <!-- Tree Indentation -->
    <div class="tree-indent" [style.margin-left.px]="level * 20">
      <!-- Expand/Collapse Icon for folders with children -->
      <div class="tree-connector" *ngIf="node.type === 'folder' && node.children && node.children.length > 0">
        <mat-icon class="expand-icon" 
                  [class.expanded]="node.expanded === true"
                  (click)="toggleFolderExpansion(node); $event.stopPropagation()"
                  matTooltip="{{node.expanded ? 'Collapse' : 'Expand'}} folder">
          expand_more
        </mat-icon>
      </div>
      <!-- Empty space for items without children -->
      <div class="tree-connector" *ngIf="!(node.type === 'folder' && node.children && node.children.length > 0)">
      </div>
      
      <!-- Node Content -->
      <div class="node-content" 
           [class.selected]="node === selectedHierarchyNode"
           [class.folder]="node.type === 'folder'"
           [class.file]="node.type === 'file'"
           (click)="onHierarchyNodeClick(node)">
        
        <!-- Node Icon and Name -->
        <div class="node-main">
          <mat-icon class="node-icon">{{ getNodeIcon(node) }}</mat-icon>
          <span class="node-name">{{ node.name }}</span>
          <span class="node-type" *ngIf="node.type === 'file'">FILE</span>
          
          <!-- File Size (for files) -->
          <span class="file-size" *ngIf="node.type === 'file' && node.size">{{ formatFileSize(node.size) }}</span>
        </div>
        
        <!-- Context Actions (as shown in your image) -->
        <div class="node-actions" *ngIf="node === selectedHierarchyNode">
          <!-- Create Subfolder -->
          <button mat-icon-button 
                  *ngIf="node.type === 'folder'"
                  (click)="onCreateFolderInPath(node.path); $event.stopPropagation()"
                  matTooltip="Create subfolder"
                  class="context-action create-action">
            <mat-icon>add</mat-icon>
          </button>
          
          <!-- Delete folder/file -->
          <button mat-icon-button 
                  (click)="onDeleteNode(node); $event.stopPropagation()"
                  matTooltip="Delete"
                  class="context-action delete-action">
            <mat-icon>delete</mat-icon>
          </button>
          
          <!-- Upload to this folder -->
          <button mat-icon-button 
                  *ngIf="node.type === 'folder'"
                  (click)="onUploadToFolder(node.path); $event.stopPropagation()"
                  matTooltip="Upload files"
                  class="context-action upload-action">
            <mat-icon>upload</mat-icon>
          </button>
          
          <!-- Download file -->
          <button mat-icon-button 
                  *ngIf="node.type === 'file'"
                  (click)="onDownloadFile(node); $event.stopPropagation()"
                  matTooltip="Download file"
                  class="context-action download-action">
            <mat-icon>download</mat-icon>
          </button>
          
          <!-- Download folder as ZIP -->
          <button mat-icon-button 
                  *ngIf="node.type === 'folder'"
                  (click)="onDownloadFolder(node); $event.stopPropagation()"
                  matTooltip="Download folder as ZIP"
                  class="context-action download-action">
            <mat-icon>folder_zip</mat-icon>
          </button>
          
          <!-- Preview folder contents -->
          <button mat-icon-button 
                  *ngIf="node.type === 'folder'"
                  (click)="onPreviewFolder(node); $event.stopPropagation()"
                  matTooltip="Preview contents"
                  class="context-action preview-action">
            <mat-icon>visibility</mat-icon>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Recursive Children (only if expanded) -->
    <ng-container *ngIf="node.children && node.children.length > 0 && node.expanded === true">
      <ng-container *ngTemplateOutlet="hierarchyNode; context: { $implicit: node.children, level: level + 1 }"></ng-container>
    </ng-container>
  </div>
</ng-template>

<!-- Enterprise Hierarchical Tree Template - Enhanced Visualization -->
<ng-template #enterpriseHierarchyNode let-nodes let-level="level">
  <div *ngFor="let node of nodes; trackBy: trackByPath" class="enterprise-hierarchy-item" [attr.data-level]="level">
    <!-- Tree Row -->
    <div class="tree-row" 
         [class.selected]="node === selectedHierarchyNode"
         [class.folder-row]="node.type === 'folder'"
         [class.file-row]="node.type === 'file'"
         [class.has-children]="node.children && node.children.length > 0"
         [class.expanded]="node.expanded === true"
         [style.padding-left.px]="16 + level * 24">
      
      <!-- Tree Lines (for visual hierarchy) -->
      <div class="tree-lines">
        <span *ngFor="let i of getTreeLevelArray(level)" class="tree-line"></span>
      </div>
      
      <!-- Expand/Collapse Toggle -->
      <div class="expand-toggle" 
           *ngIf="node.type === 'folder' && node.children && node.children.length > 0"
           (click)="toggleFolderExpansion(node); $event.stopPropagation()">
        <mat-icon class="toggle-icon" [class.rotated]="node.expanded === true">
          chevron_right
        </mat-icon>
      </div>
      <div class="expand-toggle placeholder" *ngIf="!(node.type === 'folder' && node.children && node.children.length > 0)"></div>
      
      <!-- Node Icon -->
      <div class="node-icon-wrapper" 
           [class.folder-icon]="node.type === 'folder'"
           [class.file-icon]="node.type === 'file'"
           (click)="onHierarchyNodeClick(node)">
        <mat-icon class="type-icon">{{ getEnterpriseNodeIcon(node) }}</mat-icon>
        <div class="icon-badge" *ngIf="node.type === 'folder' && node.children && node.children.length > 0">
          {{ node.children.length }}
        </div>
      </div>
      
      <!-- Node Name and Info -->
      <div class="node-info" (click)="onHierarchyNodeClick(node)">
        <span class="node-name">{{ node.name }}</span>
        <div class="node-meta">
          <span class="node-type-badge" [class.folder-badge]="node.type === 'folder'" [class.file-badge]="node.type === 'file'">
            {{ node.type === 'folder' ? 'FOLDER' : 'FILE' }}
          </span>
          <span class="file-size" *ngIf="node.type === 'file' && node.size">
            {{ formatFileSize(node.size) }}
          </span>
          <span class="child-count" *ngIf="node.type === 'folder' && node.children">
            {{ node.children.length }} item{{ node.children.length !== 1 ? 's' : '' }}
          </span>
        </div>
      </div>
      
      <!-- Quick Actions (always visible for selected items) -->
      <div class="quick-actions" [class.visible]="node === selectedHierarchyNode">
        <!-- Create Subfolder -->
        <button mat-icon-button 
                *ngIf="node.type === 'folder'"
                (click)="onCreateFolderInPath(node.path); $event.stopPropagation()"
                matTooltip="Create subfolder"
                class="quick-action create">
          <mat-icon>add</mat-icon>
        </button>
        
        <!-- Upload to folder -->
        <button mat-icon-button 
                *ngIf="node.type === 'folder'"
                (click)="onUploadToFolder(node.path); $event.stopPropagation()"
                matTooltip="Upload files"
                class="quick-action upload">
          <mat-icon>upload</mat-icon>
        </button>
        
        <!-- Download -->
        <button mat-icon-button 
                (click)="node.type === 'file' ? onDownloadFile(node) : onDownloadFolder(node); $event.stopPropagation()"
                [matTooltip]="node.type === 'file' ? 'Download file' : 'Download as ZIP'"
                class="quick-action download">
          <mat-icon>{{ node.type === 'file' ? 'download' : 'folder_zip' }}</mat-icon>
        </button>
        
        <!-- Delete -->
        <button mat-icon-button 
                (click)="onDeleteNode(node); $event.stopPropagation()"
                matTooltip="Delete"
                class="quick-action delete">
          <mat-icon>delete</mat-icon>
        </button>
      </div>
    </div>
    
    <!-- Recursive Children (only if expanded) -->
    <div class="children-container" *ngIf="node.children && node.children.length > 0 && node.expanded === true">
      <ng-container *ngTemplateOutlet="enterpriseHierarchyNode; context: { $implicit: node.children, level: level + 1 }"></ng-container>
    </div>
  </div>
</ng-template>

<!-- GCP-Style Folder Tree Template (for left sidebar) -->
<ng-template #gcpFolderTreeNode let-nodes let-level="level">
  <div *ngFor="let node of nodes" class="gcp-folder-tree-item" [attr.data-level]="level">
    <div class="folder-row"
         [class.selected]="node === selectedHierarchyNode"
         [class.expanded]="node.expanded === true"
         [style.padding-left.px]="8 + level * 16"
         (click)="onNavigateToFolder(node)">
      
      <!-- Expand/Collapse Toggle -->
      <button mat-icon-button 
              class="expand-btn"
              *ngIf="node.children && getFoldersOnly(node.children).length > 0"
              (click)="toggleFolderExpansion(node); $event.stopPropagation()">
        <mat-icon class="expand-icon" [class.rotated]="node.expanded === true">
          chevron_right
        </mat-icon>
      </button>
      <span class="expand-placeholder" *ngIf="!(node.children && getFoldersOnly(node.children).length > 0)"></span>
      
      <!-- Folder Icon -->
      <mat-icon class="folder-icon" [class.open]="node.expanded === true">
        {{ node.expanded === true ? 'folder_open' : 'folder' }}
      </mat-icon>
      
      <!-- Folder Name -->
      <span class="folder-name">{{ node.name }}</span>
      
      <!-- Item Count Badge -->
      <span class="item-count-badge" *ngIf="node.children && node.children.length > 0">
        {{ node.children.length }}
      </span>
    </div>
    
    <!-- Recursive Children -->
    <div class="folder-children" *ngIf="node.children && node.expanded === true">
      <ng-container *ngTemplateOutlet="gcpFolderTreeNode; context: { $implicit: getFoldersOnly(node.children), level: level + 1 }"></ng-container>
    </div>
  </div>
</ng-template>
